"use client";

import { useMemo, useState } from "react";
import {
  Field,
  TextInput,
  SegmentedControl,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Grounded in the standard fire-door inspection (BS 8214 installation/maintenance,
   BS 9999 management framework) and the Fire Safety (England) Regulations 2022
   inspection regime. Items mirror what a competent fire-door inspector checks:
   certification/identification, leaf and frame condition, gaps, intumescent and
   smoke seals, hinges and self-closer, glazing, signage and operation.

   `critical` items are the ones that, when defective, mean the door cannot be
   relied on as a fire door until remedied (e.g. missing intumescent strips, a
   closer that doesn't latch, a propped-open door).

   Compliance: this is a self-help checklist that drafts findings only. It is not a
   certified inspection and does not replace a competent fire-door inspector or the
   dutyholder's statutory duties. Every output carries that disclaimer. */

type State = "" | "pass" | "defect" | "na";

interface Item {
  id: string;
  label: string;
  hint?: string;
  critical?: boolean;
}
interface Group {
  id: string;
  title: string;
  items: Item[];
}

const GROUPS: Group[] = [
  {
    id: "cert",
    title: "Certification & condition",
    items: [
      { id: "cert-label", label: "Certification label or plug present, legible and matches a certified doorset", hint: "Usually on the top or hinge edge of the leaf. No identification means the door cannot be confirmed as a fire door.", critical: true },
      { id: "leaf", label: "Door leaf undamaged — no warping, holes or excessive trimming", hint: "Trimming beyond the certified tolerance (often ~3 mm per edge) voids the rating.", critical: true },
      { id: "frame", label: "Frame securely fixed, undamaged and correctly aligned" },
      { id: "rating", label: "Door rating (e.g. FD30 / FD60) is appropriate for the opening" },
    ],
  },
  {
    id: "gaps",
    title: "Gaps",
    items: [
      { id: "gap-edges", label: "Gap around the leaf consistent, around 2–4 mm", hint: "A coin or gap gauge helps. Gaps that are too wide let fire and smoke past.", critical: true },
      { id: "gap-threshold", label: "Threshold / bottom gap within specification (typically ≤ 3 mm, or per certification)" },
    ],
  },
  {
    id: "seals",
    title: "Intumescent & smoke seals",
    items: [
      { id: "intumescent", label: "Intumescent strips present, continuous and undamaged", hint: "Fitted to the leaf or frame; they expand in heat to seal the gap. Missing or painted-over strips fail.", critical: true },
      { id: "smoke-seals", label: "Cold-smoke seals (brush / fin) present and intact where required" },
    ],
  },
  {
    id: "hardware",
    title: "Hinges, closer & hardware",
    items: [
      { id: "hinges", label: "At least 3 CE/UKCA-marked hinges, all screws present and tight", critical: true },
      { id: "closer", label: "Self-closing device fitted; closes the door fully onto the latch from any position", hint: "Test from 5°, half-open and fully open. A door that doesn't fully close and latch is not an effective fire door.", critical: true },
      { id: "latch", label: "Latch / lock engages and holds the door closed" },
      { id: "no-mods", label: "No uncertified modifications, through-fixings or air-transfer grilles" },
    ],
  },
  {
    id: "operation",
    title: "Glazing, signage & operation",
    items: [
      { id: "glazing", label: "Any glazing is fire-rated with intumescent glazing seals and intact beads", hint: "Mark N/A if the door has no glazing.", critical: true },
      { id: "signage", label: "Correct signage present (e.g. “Fire door — keep shut” / “keep locked”)" },
      { id: "not-wedged", label: "Door is not wedged, propped or held open by uncertified means", critical: true },
    ],
  },
];

const ALL_ITEMS = GROUPS.flatMap((g) => g.items);

const OPTIONS = [
  { value: "pass" as const, label: "Pass", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
  { value: "defect" as const, label: "Defect", activeCls: "border-risk-veryhigh bg-risk-veryhigh/10 text-risk-veryhigh" },
  { value: "na" as const, label: "N/A", activeCls: "border-line-2 bg-mist text-muted" },
];

export function FireDoorInspectionChecklist() {
  const [loc, setLoc] = useState("");
  const [ref, setRef] = useState("");
  const [rating, setRating] = useState("");
  const [inspector, setInspector] = useState("");
  const [state, setState] = useState<Record<string, State>>({});

  const set = (id: string, v: State) => setState((s) => ({ ...s, [id]: v }));

  const result = useMemo(() => {
    const answered = ALL_ITEMS.filter((i) => state[i.id]);
    const defects = ALL_ITEMS.filter((i) => state[i.id] === "defect");
    const criticalDefects = defects.filter((i) => i.critical);
    const passes = ALL_ITEMS.filter((i) => state[i.id] === "pass").length;
    const remaining = ALL_ITEMS.length - answered.length;
    let level: Verdict = "low";
    let title = "";
    if (answered.length === 0) {
      title = "Work through each check";
    } else if (criticalDefects.length > 0) {
      level = "veryhigh";
      title = `Fails this check — ${criticalDefects.length} critical defect${criticalDefects.length > 1 ? "s" : ""}`;
    } else if (defects.length > 0) {
      level = "medium";
      title = `${defects.length} defect${defects.length > 1 ? "s" : ""} found — remedial action needed`;
    } else if (remaining === 0) {
      level = "low";
      title = "Passed every check on this checklist";
    } else {
      level = "low";
      title = `${passes} passed so far — ${remaining} left to check`;
    }
    return { defects, criticalDefects, passes, remaining, answered: answered.length, level, title };
  }, [state]);

  const showResult = result.answered > 0;
  if (showResult && result.remaining === 0) {
    trackToolResult("fire-door-inspection-checklist", { defects: result.defects.length, critical: result.criticalDefects.length });
  }

  return (
    <div className="space-y-8">
      {/* Door details */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Door location / area"><TextInput value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. Level 3 lobby, communal" /></Field>
        <Field label="Door reference"><TextInput value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. FD-014" /></Field>
        <Field label="Rating"><TextInput value={rating} onChange={(e) => setRating(e.target.value)} placeholder="e.g. FD30S" /></Field>
        <Field label="Inspected by"><TextInput value={inspector} onChange={(e) => setInspector(e.target.value)} placeholder="Name / competence" /></Field>
      </div>

      {/* Live result */}
      {showResult && (
        <ResultBanner level={result.level} title={result.title}>
          {result.criticalDefects.length > 0 ? (
            <p>One or more <strong>critical</strong> elements failed — the door cannot be relied on as a fire door until these are put right by a competent person. Record it and report to the Responsible Person.</p>
          ) : result.defects.length > 0 ? (
            <p>No critical failures, but the defects below need remedial action and a re-check. Record them against the door reference.</p>
          ) : result.remaining === 0 ? (
            <p>Every item passed or was marked N/A. Keep this record — the dutyholder needs it to evidence compliance to the fire authority and insurers.</p>
          ) : (
            <p>Keep going — answer every item for a complete record.</p>
          )}
        </ResultBanner>
      )}

      {/* Checklist */}
      {GROUPS.map((g) => (
        <fieldset key={g.id} className="rounded-2xl border border-line bg-white p-5 shadow-soft">
          <legend className="px-2 font-display text-base font-bold text-ink">{g.title}</legend>
          <div className="mt-3 space-y-4">
            {g.items.map((item) => (
              <div key={item.id} className="grid gap-2 border-b border-line pb-4 last:border-0 last:pb-0 sm:grid-cols-[1fr_auto] sm:items-start sm:gap-4">
                <div>
                  <p className="text-sm text-body">
                    {item.label}
                    {item.critical && <span className="ml-2 rounded bg-risk-veryhigh/10 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-risk-veryhigh">Critical</span>}
                  </p>
                  {item.hint && <p className="mt-1 text-xs text-muted">{item.hint}</p>}
                </div>
                <SegmentedControl
                  legend={item.label}
                  name={item.id}
                  value={state[item.id] ?? ""}
                  onChange={(v) => set(item.id, v as State)}
                  options={OPTIONS}
                  className="sm:w-[230px]"
                />
              </div>
            ))}
          </div>
        </fieldset>
      ))}

      {/* Defect summary (prints) */}
      {result.defects.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h3 className="font-display text-base font-bold text-amber-900">Defects to action {ref && `— ${ref}`}</h3>
          <ul className="mt-3 space-y-2 text-sm text-amber-900">
            {result.defects.map((d) => (
              <li key={d.id} className="flex gap-2">
                <span className="mt-0.5 shrink-0 font-bold">{d.critical ? "‼" : "•"}</span>
                <span>{d.label}{d.critical && " (critical)"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <PrintButton tool="fire-door-inspection-checklist" label="Print / save this inspection record" />
        <span className="text-xs text-muted">{result.answered}/{ALL_ITEMS.length} items checked</span>
      </div>

      <PrintFooter toolName="Fire Door Inspection Checklist" />
    </div>
  );
}
